import {
  INTEGRATION_DEPARTMENTS,
  PLATFORMS,
  PROPHET_DEPARTMENTS,
  SECTIONS,
  type PlatformKey,
} from '../constants/pageAccess';
import { roleTypes } from '../types';
import { isAllSelected, isAnySelected } from '../utils/pageAccess';

type Options = {
  value: roleTypes.PageAccessConfig;
  onChange: (value: roleTypes.PageAccessConfig) => void;
};

export const usePageAccessToggles = ({ value, onChange }: Options) => {
  const getSelected = (
    section: keyof roleTypes.PageAccessConfig,
    platform: PlatformKey
  ): roleTypes.PageAccessDepartment[] => {
    const validDepartments =
      SECTIONS.find((sectionConfig) => sectionConfig.key === section)?.departments ?? [];
    return (value[section]?.[platform] ?? []).filter((department) =>
      validDepartments.includes(department)
    );
  };

  const allFull = SECTIONS.every((section) =>
    PLATFORMS.every((platform) =>
      isAllSelected(getSelected(section.key, platform.key), section.departments)
    )
  );
  const anyChecked = SECTIONS.some((section) =>
    PLATFORMS.some((platform) =>
      isAnySelected(getSelected(section.key, platform.key), section.departments)
    )
  );

  const toggleAll = () => {
    if (allFull) {
      onChange({});
    } else {
      onChange({
        prophet: { instagram: [...PROPHET_DEPARTMENTS], telegram: [...PROPHET_DEPARTMENTS] },
        integrations: {
          instagram: [...INTEGRATION_DEPARTMENTS],
          telegram: [...INTEGRATION_DEPARTMENTS],
        },
      });
    }
  };

  const toggleSection = (section: (typeof SECTIONS)[number]) => {
    const isFull = PLATFORMS.every((platform) =>
      isAllSelected(getSelected(section.key, platform.key), section.departments)
    );
    const departments = isFull ? [] : [...section.departments];
    onChange({ ...value, [section.key]: { instagram: departments, telegram: departments } });
  };

  const togglePlatform = (section: (typeof SECTIONS)[number], platform: PlatformKey) => {
    const selected = getSelected(section.key, platform);
    const departments = isAllSelected(selected, section.departments)
      ? []
      : [...section.departments];
    onChange({ ...value, [section.key]: { ...value[section.key], [platform]: departments } });
  };

  const toggle = (
    section: keyof roleTypes.PageAccessConfig,
    platform: PlatformKey,
    department: roleTypes.PageAccessDepartment
  ) => {
    const current = getSelected(section, platform);
    const next = current.includes(department)
      ? current.filter((selected) => selected !== department)
      : [...current, department];
    onChange({ ...value, [section]: { ...value[section], [platform]: next } });
  };

  return { getSelected, allFull, anyChecked, toggleAll, toggleSection, togglePlatform, toggle };
};
